import { useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AuthContext } from "../../auth/AuthContext";
import { isSprintSteward, canForceStartStage } from "../../constants/sprintAccess";
import { normalizeUrl } from "../../utils/sprintGoalLinks";
import { getSprints } from "../../api/sprintApi";
import { SPRINT_WORK_STATUSES } from "../../constants/sprintAccess";
import { TRACKER_COLORS as C, MONO, formatDate } from "../../constants/trackerUi";
import { DayMeter, StageRail } from "./StagePipeline";
import TrackerModal from "./TrackerModal";
import {
  useAcceptStage,
  useStartStageNow,
  useMoveToStage,
  useCompleteStage,
  useBlockStage,
  useUnblockStage,
  useRejectStage,
  useReassignStage,
  usePostStageUpdate,
  useUpdateRollout,
  useEligibleOwners,
  useTrackerPortfolio,
  useUpdateRequirement,
} from "../../api/trackerApi";

const inputStyle = {
  width: "100%",
  padding: "9px 11px",
  borderRadius: 8,
  border: `1px solid ${C.line}`,
  background: C.surface,
  color: C.ink,
  fontSize: "0.85rem",
  fontFamily: "inherit",
};

const btn = (bg, color = "#fff") => ({
  border: "none",
  borderRadius: 8,
  padding: "8px 15px",
  fontSize: "0.79rem",
  fontWeight: 700,
  cursor: "pointer",
  whiteSpace: "nowrap",
  background: bg,
  color,
});

const ghostBtn = {
  ...btn(C.surface, C.inkSoft),
  border: `1px solid ${C.line}`,
};

/** Row in the ⋯ menu — full width, left aligned, coloured by consequence. */
const menuItem = (color) => ({
  display: "block",
  width: "100%",
  textAlign: "left",
  border: "none",
  background: "none",
  padding: "8px 10px",
  borderRadius: 7,
  fontSize: "0.8rem",
  fontWeight: 700,
  cursor: "pointer",
  whiteSpace: "nowrap",
  color,
});

/** Compose an update: free text plus any number of links worth sharing. */
function UpdateComposer({ requirementId, stageCode }) {
  const [body, setBody] = useState("");
  const [links, setLinks] = useState([]);
  const [linkDraft, setLinkDraft] = useState("");
  const [showLink, setShowLink] = useState(false);
  const post = usePostStageUpdate();

  /**
   * Only accept something that is actually a link.
   *
   * These get rendered as anchors and mirrored into the story's activity feed,
   * so a stray note saved as a URL becomes a dead link everyone else clicks.
   * normalizeUrl is the same extractor the sprint goal links use, so "see
   * https://x/y" yields the URL rather than being rejected outright.
   */
  const addLink = () => {
    const raw = linkDraft.trim();
    if (!raw) return;
    const url = normalizeUrl(raw);
    if (!url) {
      toast.error("That does not look like a link — include http:// or https://");
      return;
    }
    if (links.some((l) => l.url === url)) {
      toast.error("That link is already attached");
      setLinkDraft("");
      return;
    }
    setLinks((prev) => [...prev, { url }]);
    setLinkDraft("");
    setShowLink(false);
  };

  const submit = async () => {
    if (!body.trim() && links.length === 0) {
      toast.error("Write something or add a link");
      return;
    }
    try {
      await post.mutateAsync({ id: requirementId, stageCode, body: body.trim() || null, links });
      setBody("");
      setLinks([]);
      toast.success("Update posted");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not post update");
    }
  };

  return (
    <div style={{ borderTop: `1px solid ${C.line}`, background: C.sunken, padding: "14px 16px" }}>
      <div
        style={{
          fontFamily: MONO,
          fontSize: "0.6rem",
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: C.inkFaint,
          marginBottom: 8,
        }}
      >
        Post an update
      </div>
      <div style={{ background: C.surface, border: `1px solid ${C.line}`, borderRadius: 11, overflow: "hidden" }}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What moved today? Anything blocking you?"
          rows={2}
          style={{
            width: "100%",
            border: "none",
            background: "transparent",
            resize: "vertical",
            minHeight: 54,
            padding: "11px 13px",
            fontFamily: "inherit",
            fontSize: "0.82rem",
            color: C.ink,
            lineHeight: 1.5,
            outline: "none",
          }}
        />

        {links.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "0 13px 10px" }}>
            {links.map((l, i) => (
              <span
                key={i}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: C.accentSoft,
                  color: C.accentInk,
                  borderRadius: 100,
                  padding: "4px 10px",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  maxWidth: "100%",
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
                  {l.url}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setLinks((prev) => prev.filter((_, j) => j !== i))}
                  onKeyDown={(e) => e.key === "Enter" && setLinks((prev) => prev.filter((_, j) => j !== i))}
                  style={{ cursor: "pointer", opacity: 0.6, fontWeight: 800 }}
                >
                  ×
                </span>
              </span>
            ))}
          </div>
        ) : null}

        {showLink ? (
          <div style={{ display: "flex", gap: 7, padding: "0 13px 11px" }}>
            <input
              autoFocus
              value={linkDraft}
              onChange={(e) => setLinkDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addLink()}
              placeholder="Paste any link — Figma, Drive, Confluence, Bitbucket…"
              style={{ ...inputStyle, flex: 1 }}
            />
            <button onClick={addLink} style={btn(C.ink, C.surface)}>
              Add
            </button>
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "9px 11px",
            borderTop: `1px solid ${C.lineSoft}`,
            flexWrap: "wrap",
          }}
        >
          <button onClick={() => setShowLink((v) => !v)} style={{ ...ghostBtn, padding: "6px 11px", fontSize: "0.74rem" }}>
            🔗 Add link
          </button>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: "0.67rem", color: C.inkFaint }}>Posts here and to the linked story</span>
          <button onClick={submit} disabled={post.isPending} style={btn(C.accent)}>
            {post.isPending ? "Posting…" : "Post update"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** "A", "A and B", "A, B and C" — never a run of "and"s for 3+ items. */
function joinWithAnd(items) {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** Pick who takes the next stage, and by when. */
function HandoffForm({ requirementId, stageCode, projectId, nextStage, stages, canSkip, onDone, onCancel }) {
  /**
   * Where it can go. Every stage after this one, so a manager can send work
   * past steps it genuinely does not need — a performance fix that needs no
   * design should not have to open and close Design to reach Development.
   *
   * Forward only: sending it back is a rejection, which has its own action
   * and its own reason.
   */
  const forwardStages = useMemo(() => {
    const all = stages || [];
    const here = all.findIndex((s) => s.stageCode === stageCode);
    if (here === -1) return [];
    return all.slice(here + 1);
  }, [stages, stageCode]);

  const [targetCode, setTargetCode] = useState(nextStage?.stageCode || "");
  const targetStage =
    forwardStages.find((s) => s.stageCode === targetCode) || nextStage || null;
  const isSkipping = Boolean(
    nextStage && targetCode && targetCode !== nextStage.stageCode
  );
  const skipped = isSkipping
    ? forwardStages.slice(0, forwardStages.findIndex((s) => s.stageCode === targetCode))
    : [];

  const { data: owners } = useEligibleOwners(projectId, targetStage?.stageCode);
  const [ownerId, setOwnerId] = useState("");
  const [eta, setEta] = useState(nextStage?.suggestedEtaDate || "");
  /**
   * Whether the person actually chose this date, rather than it being the
   * suggestion this form opened with.
   *
   * The suggestion is a snapshot from whenever the card was last fetched —
   * often while the stage being completed is still open. If that stage then
   * finishes early (a 9-day Development done in 1), the snapshot is stale:
   * it projected the next stage's window from the old, un-accelerated
   * timeline. Sending it anyway would lock in a date that has nothing to do
   * with when this stage actually finished.
   *
   * So only an explicit choice is sent. Left untouched, the next stage is
   * handed over with no committed date at all, and its own suggestion —
   * computed fresh once this handoff's real completion time is on record —
   * is what its owner sees when they accept.
   */
  const [etaTouched, setEtaTouched] = useState(false);
  const [note, setNote] = useState("");
  const [documentUrl, setDocumentUrl] = useState("");
  const complete = useCompleteStage();

  /* The people who can take Design are not the people who take Development,
     and the standard runway differs too — so both reset when the target does,
     rather than silently carrying over someone ineligible. */
  const changeTarget = (next) => {
    setTargetCode(next);
    setOwnerId("");
    const stage = forwardStages.find((s) => s.stageCode === next);
    setEta(stage?.suggestedEtaDate || "");
    setEtaTouched(false);
  };

  const submit = async () => {
    if (nextStage && !ownerId) {
      toast.error("Choose who takes it next");
      return;
    }
    try {
      await complete.mutateAsync({
        id: requirementId,
        stageCode,
        note: note.trim() || null,
        documentUrl: documentUrl.trim() || null,
        nextOwnerId: ownerId ? Number(ownerId) : null,
        nextEtaDate: etaTouched ? eta || null : null,
        // Only sent when it differs from the natural next stage, so an
        // ordinary handoff posts exactly the body it always did.
        nextStageCode: isSkipping ? targetCode : undefined,
      });
      toast.success(
        nextStage ? `Handed to ${targetStage?.label || nextStage.label}` : "Requirement completed"
      );
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not complete stage");
    }
  };

  const fieldLabel = {
    display: "block",
    fontFamily: MONO,
    fontSize: "0.58rem",
    textTransform: "uppercase",
    color: C.inkFaint,
    marginBottom: 5,
  };

  return (
    <TrackerModal
      title={
        nextStage
          ? `Hand off to ${targetStage?.label || nextStage.label}`
          : "Close out this requirement"
      }
      subtitle={
        nextStage
          ? isSkipping
            ? `${joinWithAnd(skipped.map((x) => x.label))} will be marked skipped.`
            : "They get it at their target date, and this stage closes."
          : "This is the last stage — completing it closes the requirement."
      }
      onClose={onCancel}
      footer={
        <>
          <button onClick={submit} disabled={complete.isPending} style={btn(C.accent)}>
            {complete.isPending ? "Saving…" : nextStage ? "Complete & hand off →" : "Mark complete"}
          </button>
          <button onClick={onCancel} style={ghostBtn}>
            Cancel
          </button>
        </>
      }
    >
      {nextStage && canSkip && forwardStages.length > 1 ? (
        <label style={{ display: "block", marginBottom: 12 }}>
          <span style={fieldLabel}>Hand off to</span>
          <select value={targetCode} onChange={(e) => changeTarget(e.target.value)} style={inputStyle}>
            {forwardStages.map((st, i) => (
              <option key={st.stageCode} value={st.stageCode}>
                {st.label}
                {i === 0 ? " — next" : ` — skips ${forwardStages.slice(0, i).map((x) => x.label).join(", ")}`}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {nextStage ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(190px, 100%), 1fr))", gap: 12, marginBottom: 12 }}>
          <label>
            <span style={fieldLabel}>Next owner</span>
            <select autoFocus value={ownerId} onChange={(e) => setOwnerId(e.target.value)} style={inputStyle}>
              <option value="">Choose someone…</option>
              {(owners || []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.preferred ? " ★" : ""} — {m.role}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span style={fieldLabel}>Their target date</span>
            <input
              type="date"
              value={eta}
              onChange={(e) => {
                setEta(e.target.value);
                setEtaTouched(true);
              }}
              style={inputStyle}
              placeholder={eta ? undefined : "Owner sets one on accept"}
            />
            {!etaTouched ? (
              <span style={{ display: "block", fontSize: "0.68rem", color: C.inkFaint, marginTop: 4 }}>
                Shown as a guide — left as-is, the owner picks their own date when they accept.
              </span>
            ) : null}
          </label>
        </div>
      ) : null}

      <label style={{ display: "block", marginBottom: 12 }}>
        <span style={fieldLabel}>Link a doc / BRD (optional)</span>
        <input value={documentUrl} onChange={(e) => setDocumentUrl(e.target.value)} style={inputStyle} />
      </label>

      <label style={{ display: "block" }}>
        <span style={fieldLabel}>Closing note (optional)</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          style={{ ...inputStyle, resize: "vertical" }}
        />
      </label>
    </TrackerModal>
  );
}

/**
 * Move the requirement to any later stage. The current stage and every stage
 * in between are marked completed and written to the activity log under the
 * mover's name - this is the admin override, not the owner finishing work.
 */
function MoveToStageForm({ requirementId, stageCode, projectId, stages, onDone, onCancel }) {
  const forwardStages = useMemo(() => {
    const all = stages || [];
    const here = all.findIndex((s) => s.stageCode === stageCode);
    return here === -1 ? [] : all.slice(here + 1);
  }, [stages, stageCode]);
  const currentStage = (stages || []).find((s) => s.stageCode === stageCode);

  const [targetCode, setTargetCode] = useState(forwardStages[0]?.stageCode || "");
  const targetIdx = forwardStages.findIndex((s) => s.stageCode === targetCode);
  // The stage it is on now, plus everything it passes over.
  const completing = [currentStage, ...forwardStages.slice(0, Math.max(targetIdx, 0))].filter(Boolean);
  const target = forwardStages[targetIdx] || null;

  const { data: owners } = useEligibleOwners(projectId, targetCode);
  const [ownerId, setOwnerId] = useState("");
  const [eta, setEta] = useState("");
  const [note, setNote] = useState("");
  const move = useMoveToStage();

  const changeTarget = (next) => {
    setTargetCode(next);
    // Who can take Design is not who can take QA - never carry one across.
    setOwnerId("");
  };

  const submit = async () => {
    if (!ownerId) {
      toast.error("Choose who takes it");
      return;
    }
    try {
      await move.mutateAsync({
        id: requirementId,
        targetStageCode: targetCode,
        ownerId: Number(ownerId),
        etaDate: eta || null,
        note: note.trim() || null,
      });
      toast.success(`Moved to ${target?.label || targetCode}`);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not move it");
    }
  };

  const fieldLabel = {
    display: "block",
    fontFamily: MONO,
    fontSize: "0.58rem",
    textTransform: "uppercase",
    color: C.inkFaint,
    marginBottom: 5,
  };

  return (
    <TrackerModal
      title="Move to stage"
      subtitle={
        completing.length
          ? `Marks the ${joinWithAnd(completing.map((x) => x.label))} stage${completing.length > 1 ? "s" : ""} as completed and logs ${completing.length > 1 ? "each" : "it"}.`
          : ""
      }
      onClose={onCancel}
      footer={
        <>
          <button onClick={submit} disabled={move.isPending || !target} style={btn(C.accent)}>
            {move.isPending ? "Moving…" : `Move to ${target?.label || "stage"} →`}
          </button>
          <button onClick={onCancel} style={ghostBtn}>
            Cancel
          </button>
        </>
      }
    >
      <label style={{ display: "block", marginBottom: 12 }}>
        <span style={fieldLabel}>Move to</span>
        <select value={targetCode} onChange={(e) => changeTarget(e.target.value)} style={inputStyle}>
          {forwardStages.map((st, i) => (
            <option key={st.stageCode} value={st.stageCode}>
              {st.label}
              {i === 0 ? " — next stage" : ` — also completes ${forwardStages.slice(0, i).map((x) => x.label).join(", ")}`}
            </option>
          ))}
        </select>
      </label>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(190px, 100%), 1fr))", gap: 12, marginBottom: 12 }}>
        <label>
          <span style={fieldLabel}>Who takes {target?.label || "it"}</span>
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} style={inputStyle}>
            <option value="">Choose someone…</option>
            {(owners || []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.preferred ? " ★" : ""} — {m.role}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span style={fieldLabel}>Target date (optional)</span>
          <input type="date" value={eta} onChange={(e) => setEta(e.target.value)} style={inputStyle} />
        </label>
      </div>

      <label style={{ display: "block" }}>
        <span style={fieldLabel}>Reason (optional, shown in the activity log)</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          style={{ ...inputStyle, resize: "vertical" }}
        />
      </label>
    </TrackerModal>
  );
}

/** Hand an unaccepted stage to someone else, optionally with a new date. */
function ReassignForm({
  requirementId,
  stageCode,
  projectId,
  currentOwnerId,
  currentOwnerName,
  currentEta,
  onDone,
  onCancel,
}) {
  const { data: owners } = useEligibleOwners(projectId, stageCode);
  const [ownerId, setOwnerId] = useState("");
  const [eta, setEta] = useState(currentEta || "");
  const [note, setNote] = useState("");
  const reassign = useReassignStage();

  const submit = async () => {
    if (!ownerId) {
      toast.error("Choose who should take it");
      return;
    }
    try {
      await reassign.mutateAsync({
        id: requirementId,
        stageCode,
        ownerId: Number(ownerId),
        etaDate: eta || null,
        note: note.trim() || null,
      });
      toast.success("Reassigned");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not reassign");
    }
  };

  const fieldLabel = {
    display: "block",
    fontFamily: MONO,
    fontSize: "0.58rem",
    textTransform: "uppercase",
    color: C.inkFaint,
    marginBottom: 5,
  };

  return (
    <TrackerModal
      title="Change assignee"
      subtitle={
        currentOwnerName
          ? `${currentOwnerName} has it now — whoever takes it starts fresh.`
          : "Whoever takes it starts fresh."
      }
      onClose={onCancel}
      footer={
        <>
          <button onClick={submit} disabled={reassign.isPending} style={btn(C.accent)}>
            {reassign.isPending ? "Saving…" : "Reassign"}
          </button>
          <button onClick={onCancel} style={ghostBtn}>
            Cancel
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(190px, 100%), 1fr))", gap: 12, marginBottom: 12 }}>
        <label>
          <span style={fieldLabel}>Hand it to</span>
          <select autoFocus value={ownerId} onChange={(e) => setOwnerId(e.target.value)} style={inputStyle}>
            <option value="">Choose someone…</option>
            {(owners || [])
              .filter((m) => Number(m.id) !== Number(currentOwnerId))
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.preferred ? " ★" : ""} — {m.role}
                </option>
              ))}
          </select>
        </label>
        <label>
          <span style={fieldLabel}>Their target date</span>
          <input type="date" value={eta} onChange={(e) => setEta(e.target.value)} style={inputStyle} />
        </label>
      </div>

      <label style={{ display: "block" }}>
        <span style={fieldLabel}>Why the change? (optional)</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} style={inputStyle} />
      </label>
    </TrackerModal>
  );
}

/** A short inline form for the actions that need a reason. */
/**
 * Fix a requirement's own details — title, which project it's filed under,
 * priority, description, target date. Steward-only: raising a requirement
 * gets it wrong sometimes (the wrong sibling project, a typo in the title),
 * and that is a correction, not something the pipeline's owner/accept flow
 * covers.
 *
 * The project list is scoped to the requirement's own department — the
 * server enforces the same rule, so this only prevents an edit the server
 * would reject anyway, rather than being the actual guard.
 */
function EditRequirementForm({ requirement, onDone, onCancel }) {
  const [title, setTitle] = useState(requirement.title || "");
  const [projectId, setProjectId] = useState(requirement.projectId || "");
  const [priority, setPriority] = useState(requirement.priority || "Medium");
  const [description, setDescription] = useState(requirement.description || "");
  const [targetDate, setTargetDate] = useState(requirement.targetDate || "");
  const update = useUpdateRequirement();

  const { data: portfolio } = useTrackerPortfolio();
  const sameDeptProjects = (portfolio?.projects || []).filter(
    (p) => p.department === requirement.department
  );

  const submit = async () => {
    if (!title.trim()) {
      toast.error("Give it a title");
      return;
    }
    try {
      await update.mutateAsync({
        id: requirement.id,
        title: title.trim(),
        projectId,
        priority,
        description: description.trim() || null,
        targetDate: targetDate || null,
      });
      toast.success("Requirement updated");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not update requirement");
    }
  };

  const fieldLabel = {
    display: "block",
    fontFamily: MONO,
    fontSize: "0.58rem",
    textTransform: "uppercase",
    color: C.inkFaint,
    marginBottom: 5,
  };

  return (
    <TrackerModal
      title="Edit requirement"
      subtitle="Fix the title, project, or priority — recorded in the activity log."
      onClose={onCancel}
      footer={
        <>
          <button onClick={submit} disabled={update.isPending} style={btn(C.accent)}>
            {update.isPending ? "Saving…" : "Save changes"}
          </button>
          <button onClick={onCancel} style={ghostBtn}>
            Cancel
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <label>
          <span style={fieldLabel}>Title</span>
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} />
        </label>

        <label>
          <span style={fieldLabel}>Project</span>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={inputStyle}>
            {sameDeptProjects.map((p) => (
              <option key={p.projectId} value={p.projectId}>
                {p.projectName}
              </option>
            ))}
          </select>
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(190px, 100%), 1fr))", gap: 12 }}>
          <label>
            <span style={fieldLabel}>Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} style={inputStyle}>
              {["Critical", "High", "Medium", "Low"].map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </label>
          <label>
            <span style={fieldLabel}>Target date</span>
            <input
              type="date"
              value={targetDate || ""}
              onChange={(e) => setTargetDate(e.target.value)}
              style={inputStyle}
            />
          </label>
        </div>

        <label>
          <span style={fieldLabel}>Description</span>
          <textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </label>
      </div>
    </TrackerModal>
  );
}

function ReasonForm({ label, tone, onSubmit, onCancel, pending }) {
  const [reason, setReason] = useState("");
  return (
    <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.line}`, background: C.sunken }}>
      <input
        autoFocus
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={label}
        style={{ ...inputStyle, marginBottom: 8 }}
      />
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={() => {
            if (!reason.trim()) {
              toast.error("A reason is required");
              return;
            }
            onSubmit(reason.trim());
          }}
          disabled={pending}
          style={btn(tone)}
        >
          {pending ? "Saving…" : "Confirm"}
        </button>
        <button onClick={onCancel} style={ghostBtn}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * The panel for the stage a requirement is currently sitting in — the only
 * place work actually moves. Everything else on the card is history.
 */
export default function StagePanel({ requirement, stage, nextStage, usesSprints = false }) {
  const [mode, setMode] = useState(null); // 'handoff' | 'block' | 'reject' | 'reassign'
  const [menuOpen, setMenuOpen] = useState(false);
  const [etaDraft, setEtaDraft] = useState(stage?.etaDate || stage?.suggestedEtaDate || "");
  const [acceptNote, setAcceptNote] = useState("");
  const [acceptSprintId, setAcceptSprintId] = useState("");

  const accept = useAcceptStage();
  const startNow = useStartStageNow();
  const block = useBlockStage();
  const unblock = useUnblockStage();
  const reject = useRejectStage();
  const rollout = useUpdateRollout();

  const { user } = useContext(AuthContext);
  const canReassign = isSprintSteward(user?.role);
  const canForceStart = canForceStartStage(user?.role);
  // Same three roles as "Start now": it overrides someone else's stage. Only
  // offered while there is a later stage to move to.
  const stageIdx = (requirement?.stages || []).findIndex((s) => s.stageCode === stage?.stageCode);
  const canMoveStage =
    canForceStart && stageIdx !== -1 && stageIdx < (requirement?.stages || []).length - 1;

  // Sprint stewards can schedule the whole requirement while accepting.
  // Everyone else accepts exactly as before — the control is simply absent.
  // Only where the department runs sprint cycles — Settings → Departments
  // decides that, and a Marketing or CSM stage has nothing to schedule into.
  const canSchedule = usesSprints && isSprintSteward(user?.role);
  const { data: sprintData } = useQuery({
    queryKey: ["sprints", requirement?.projectId],
    queryFn: () => getSprints(requirement.projectId).then((r) => r.data),
    enabled: Boolean(canSchedule && requirement?.projectId),
    staleTime: 5 * 60 * 1000,
  });
  const schedulableSprints = (Array.isArray(sprintData) ? sprintData : sprintData?.sprints || [])
    .filter((sp) => SPRINT_WORK_STATUSES.includes(sp.status));

  const isPendingAccept = stage?.status === "pending_accept";
  const isBlocked = stage?.status === "blocked";
  const etaMoved = stage?.etaDate && etaDraft && etaDraft !== stage.etaDate;

  const doAccept = async () => {
    if (etaMoved && !acceptNote.trim()) {
      toast.error("Add a note explaining the new date");
      return;
    }
    try {
      await accept.mutateAsync({
        id: requirement.id,
        stageCode: stage.stageCode,
        etaDate: etaDraft || null,
        note: acceptNote.trim() || null,
        sprintId: acceptSprintId || null,
      });
      setAcceptNote("");
      setAcceptSprintId("");
      toast.success(
        acceptSprintId
          ? "Accepted and scheduled — it's on the sprint board now"
          : "Accepted — the clock starts now"
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not accept");
    }
  };

  const doStartNow = async () => {
    try {
      await startNow.mutateAsync({ id: requirement.id, stageCode: stage.stageCode });
      toast.success(`Started — no longer waiting on ${stage?.ownerName || "the owner"}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not start this stage");
    }
  };

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px 0", flexWrap: "wrap" }}>
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: isBlocked ? C.warn : C.accent,
            boxShadow: `0 0 0 3px ${isBlocked ? C.warnSoft : C.accentSoft}`,
          }}
        />
        <h3 style={{ fontSize: "0.92rem", fontWeight: 700, margin: 0 }}>{stage?.label}</h3>
        <span style={{ fontSize: "0.76rem", color: C.inkFaint }}>
          {stage?.ownerName ? (
            <>
              owned by <b style={{ color: C.inkSoft }}>{stage.ownerName}</b>
              {stage.ownerRole ? ` · ${stage.ownerRole}` : ""}
            </>
          ) : (
            "unassigned"
          )}
        </span>
        {stage?.story ? (
          <span
            style={{
              marginLeft: "auto",
              fontSize: "0.68rem",
              fontFamily: MONO,
              color: C.accentInk,
              background: C.accentSoft,
              padding: "3px 9px",
              borderRadius: 100,
            }}
            title="Where the linked story sits"
          >
            {stage.story.sprintName ? `in ${stage.story.sprintName}` : "in backlog"} · #{stage.story.issueId}
          </span>
        ) : null}
        {canMoveStage ? (
          <button
            onClick={() => setMode(mode === "move" ? null : "move")}
            title="Move this requirement to a later stage, marking the stages passed as completed"
            style={{ ...ghostBtn, padding: "5px 11px", fontSize: "0.72rem", marginLeft: stage?.story ? 0 : "auto" }}
          >
            ⇥ Move to stage…
          </button>
        ) : null}
      </div>

      <div style={{ padding: "12px 16px 14px" }}>
        <DayMeter stage={stage} />
      </div>

      {isBlocked && stage?.blockedReason ? (
        <div
          style={{
            margin: "0 16px 12px",
            padding: "9px 12px",
            borderRadius: 9,
            background: C.warnSoft,
            color: C.warn,
            fontSize: "0.78rem",
            fontWeight: 600,
          }}
        >
          ⛔ {stage.blockedReason}
        </div>
      ) : null}

      <div style={{ padding: "0 16px 12px" }}>
        <StageRail stages={requirement.stages} currentCode={requirement.stageCode} />
      </div>

      {/* Accepting is the one action that needs its own controls inline. */}
      {isPendingAccept ? (
        /* Accepting commits someone to a date, so only they can do it. A
           manager can reassign the stage, but not accept on their behalf. */
        stage?.isMine ? (
          <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.line}`, background: C.sunken }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
              <label>
                <span style={{ display: "block", fontFamily: MONO, fontSize: "0.58rem", textTransform: "uppercase", color: C.inkFaint, marginBottom: 5 }}>
                  Your target date
                </span>
                <input type="date" value={etaDraft} onChange={(e) => setEtaDraft(e.target.value)} style={{ ...inputStyle, width: 170 }} />
              </label>

              {canSchedule && schedulableSprints.length > 0 ? (
                <label>
                  <span style={{ display: "block", fontFamily: MONO, fontSize: "0.58rem", textTransform: "uppercase", color: C.inkFaint, marginBottom: 5 }}>
                    Put into sprint
                  </span>
                  <select
                    value={acceptSprintId}
                    onChange={(e) => setAcceptSprintId(e.target.value)}
                    style={{ ...inputStyle, width: 190 }}
                  >
                    <option value="">Keep in backlog</option>
                    {schedulableSprints.map((sp) => (
                      <option key={sp.id} value={sp.id}>
                        {sp.sprint_name}
                        {sp.status ? ` (${sp.status})` : ""}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <button onClick={doAccept} disabled={accept.isPending} style={btn(C.done)}>
                {accept.isPending ? "Accepting…" : "✓ Accept"}
              </button>

              {/* Declining goes behind ⋯ for the same reason it does once the
                  stage is running — it needs a reason and shouldn't be a
                  neighbour of the button you actually mean to press. */}
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label="More actions"
                  style={{ ...ghostBtn, padding: "8px 13px", fontSize: "1rem", lineHeight: 1 }}
                >
                  ⋯
                </button>
                {menuOpen ? (
                  <>
                    <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                    <div
                      role="menu"
                      style={{
                        position: "absolute",
                        bottom: "calc(100% + 6px)",
                        left: 0,
                        zIndex: 41,
                        background: C.surface,
                        border: `1px solid ${C.line}`,
                        borderRadius: 10,
                        boxShadow: "0 8px 24px rgba(16,24,43,0.16)",
                        padding: 5,
                        minWidth: 176,
                      }}
                    >
                      {canReassign ? (
                        <button
                          role="menuitem"
                          onClick={() => {
                            setMenuOpen(false);
                            setMode("edit");
                          }}
                          style={menuItem(C.inkSoft)}
                        >
                          ✎ Edit requirement
                        </button>
                      ) : null}
                      <button
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          setMode("reject");
                        }}
                        style={menuItem(C.danger)}
                      >
                        ✕ Send back
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
            {etaMoved ? (
              <input
                value={acceptNote}
                onChange={(e) => setAcceptNote(e.target.value)}
                placeholder={`Why the change from ${formatDate(stage.etaDate)}? (required)`}
                style={{ ...inputStyle, marginTop: 8, borderColor: C.warn }}
              />
            ) : null}
          </div>
        ) : (
          /* Not yours to accept. There's nothing to send back either — nobody
             has taken it on yet — so the useful controls here are reassigning
             it, or (for admin/superadmin/manager) starting it without
             waiting for that person to accept at all. */
          <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.line}`, background: C.sunken }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: "0.8rem", color: C.inkSoft }}>
                Waiting for <b>{stage?.ownerName || "the owner"}</b> to accept
                {stage?.etaDate ? ` — proposed ${formatDate(stage.etaDate)}` : ""}.
              </span>
              <span style={{ flex: 1 }} />
              {canForceStart ? (
                <button
                  onClick={doStartNow}
                  disabled={startNow.isPending}
                  title={`Start this now, without waiting for ${stage?.ownerName || "the owner"} to accept`}
                  style={btn(C.done)}
                >
                  {startNow.isPending ? "Starting…" : "▶ Start now"}
                </button>
              ) : null}
              {canReassign ? (
                <button onClick={() => setMode(mode === "reassign" ? null : "reassign")} style={ghostBtn}>
                  ✎ Change assignee
                </button>
              ) : null}
            </div>

            {mode === "reassign" ? (
              <ReassignForm
                requirementId={requirement.id}
                stageCode={stage.stageCode}
                projectId={requirement.projectId}
                currentOwnerId={stage.ownerId}
                currentOwnerName={stage.ownerName}
                currentEta={stage.etaDate}
                onDone={() => setMode(null)}
                onCancel={() => setMode(null)}
              />
            ) : null}
          </div>
        )
      ) : stage?.isMine ? (
        /* Moving the work forward is the button; the ways it can go wrong sit
           behind ⋯ so they're deliberate rather than a stray click. Unblocking
           stays out in the open — it's the way back to normal. */
        <div style={{ display: "flex", gap: 7, padding: "12px 16px", flexWrap: "wrap", borderTop: `1px solid ${C.line}`, position: "relative" }}>
          {/* Opens the dialog rather than committing — the ellipsis says more
              input is coming, so a click can't be mistaken for a done deal. */}
          <button onClick={() => setMode(mode === "handoff" ? null : "handoff")} style={btn(C.accent)}>
            {nextStage ? `Complete & hand off…` : "Mark complete…"}
          </button>

          {isBlocked ? (
            <button
              onClick={async () => {
                try {
                  await unblock.mutateAsync({ id: requirement.id, stageCode: stage.stageCode });
                  toast.success("Unblocked");
                } catch (err) {
                  toast.error(err.response?.data?.message || "Could not unblock");
                }
              }}
              style={btn(C.warn)}
            >
              Unblock
            </button>
          ) : null}

          <div style={{ position: "relative" }}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="More actions"
              style={{ ...ghostBtn, padding: "8px 13px", fontSize: "1rem", lineHeight: 1 }}
            >
              ⋯
            </button>

            {menuOpen ? (
              <>
                {/* click-away catcher */}
                <div
                  onClick={() => setMenuOpen(false)}
                  style={{ position: "fixed", inset: 0, zIndex: 40 }}
                />
                <div
                  role="menu"
                  style={{
                    position: "absolute",
                    bottom: "calc(100% + 6px)",
                    left: 0,
                    zIndex: 41,
                    background: C.surface,
                    border: `1px solid ${C.line}`,
                    borderRadius: 10,
                    boxShadow: "0 8px 24px rgba(16,24,43,0.16)",
                    padding: 5,
                    minWidth: 176,
                  }}
                >
                  {canReassign ? (
                    <button
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        setMode("edit");
                      }}
                      style={menuItem(C.inkSoft)}
                    >
                      ✎ Edit requirement
                    </button>
                  ) : null}
                  {!isBlocked ? (
                    <button
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        setMode("block");
                      }}
                      style={menuItem(C.warn)}
                    >
                      ⛔ Block
                    </button>
                  ) : null}
                  <button
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      setMode("reject");
                    }}
                    style={menuItem(C.danger)}
                  >
                    ✕ Send back
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : (
        /* Someone else's stage, already underway. Moving it on is theirs to
           do — what a manager can do is hand it to someone else. */
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.line}`, background: C.sunken }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.8rem", color: C.inkSoft }}>
              <b>{stage?.ownerName || "The owner"}</b> is working on this
              {isBlocked ? " — currently blocked" : ""}.
            </span>
            <span style={{ flex: 1 }} />
            {canReassign ? (
              <button onClick={() => setMode(mode === "reassign" ? null : "reassign")} style={ghostBtn}>
                ✎ Change assignee
              </button>
            ) : null}
          </div>

          {mode === "reassign" ? (
            <ReassignForm
              requirementId={requirement.id}
              stageCode={stage.stageCode}
              projectId={requirement.projectId}
              currentOwnerId={stage.ownerId}
              currentOwnerName={stage.ownerName}
              currentEta={stage.etaDate}
              onDone={() => setMode(null)}
              onCancel={() => setMode(null)}
            />
          ) : null}
        </div>
      )}

      {mode === "handoff" ? (
        <HandoffForm
          requirementId={requirement.id}
          stageCode={stage.stageCode}
          projectId={requirement.projectId}
          nextStage={nextStage}
          stages={requirement.stages}
          canSkip={canReassign}
          onDone={() => setMode(null)}
          onCancel={() => setMode(null)}
        />
      ) : null}

      {mode === "move" ? (
        <MoveToStageForm
          requirementId={requirement.id}
          stageCode={stage.stageCode}
          projectId={requirement.projectId}
          stages={requirement.stages}
          onDone={() => setMode(null)}
          onCancel={() => setMode(null)}
        />
      ) : null}

      {mode === "edit" ? (
        <EditRequirementForm
          requirement={requirement}
          onDone={() => setMode(null)}
          onCancel={() => setMode(null)}
        />
      ) : null}

      {mode === "block" ? (
        <ReasonForm
          label="What's blocking this?"
          tone={C.warn}
          pending={block.isPending}
          onCancel={() => setMode(null)}
          onSubmit={async (reason) => {
            try {
              await block.mutateAsync({ id: requirement.id, stageCode: stage.stageCode, reason });
              toast.success("Flagged as blocked");
              setMode(null);
            } catch (err) {
              toast.error(err.response?.data?.message || "Could not block");
            }
          }}
        />
      ) : null}

      {mode === "reject" ? (
        <ReasonForm
          label="Why are you sending this back?"
          tone={C.danger}
          pending={reject.isPending}
          onCancel={() => setMode(null)}
          onSubmit={async (reason) => {
            try {
              await reject.mutateAsync({ id: requirement.id, stageCode: stage.stageCode, reason });
              toast.success("Sent back");
              setMode(null);
            } catch (err) {
              toast.error(err.response?.data?.message || "Could not send back");
            }
          }}
        />
      ) : null}

      {/* Release tracks a rollout percentage on its way to 100. */}
      {stage?.stageCode === "release" && stage.status === "accepted" ? (
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.line}`, background: C.sunken }}>
          <div style={{ fontFamily: MONO, fontSize: "0.58rem", textTransform: "uppercase", color: C.inkFaint, marginBottom: 7 }}>
            Rollout {stage.rolloutPercent != null ? `— ${stage.rolloutPercent}%` : ""}
          </div>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {[25, 50, 75, 100].map((pct) => (
              <button
                key={pct}
                onClick={async () => {
                  try {
                    await rollout.mutateAsync({ id: requirement.id, rolloutPercent: pct });
                    toast.success(`Rollout at ${pct}%`);
                  } catch (err) {
                    toast.error(err.response?.data?.message || "Could not update rollout");
                  }
                }}
                style={{
                  ...ghostBtn,
                  padding: "6px 13px",
                  fontSize: "0.74rem",
                  ...(stage.rolloutPercent === pct ? { background: C.accent, color: "#fff", borderColor: C.accent } : null),
                }}
              >
                {pct}%
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Posting an update is the owner reporting on their own stage — nobody
          else, including an admin, who is reading rather than reporting.

          Hidden rather than disabled until the stage is accepted: there is
          nothing to report before the clock starts, and a dead textarea next
          to a working link box just reads as broken. The accept box above is
          the action at that point. */}
      {stage?.isMine && !isPendingAccept ? (
        <UpdateComposer requirementId={requirement.id} stageCode={stage.stageCode} />
      ) : null}
    </div>
  );
}
